import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { AiOutlineInfoCircle, AiOutlinePlus } from 'react-icons/ai'
import { BiSolidTrash } from 'react-icons/bi'
import { CiSearch } from 'react-icons/ci'
import { useNavigate } from 'react-router-dom'

import { patientProfilesApi } from '../../api/services/dd_panel_patient_profiles'
import { PatientProfileResponse } from '../../api/services/dd_panel_patient_profiles/types.ts'
import { PatientProfile } from '../../api/services/orders/types.ts'
import { useAuth } from '../../context/auth'
import { usePatientProfileMutations } from '../../hooks/usePatientProfileMutations.ts'
import {
  setPatientProfileTableActions,
  setPatientProfileTableColumns
} from '../../utils/patientProfileTableSettings.tsx'
import {
  ActionPopupMenu,
  ActionPopupMenuItem
} from '../common/ActionPopupMenuItem'
import Alert from '../common/Alert/Alert.tsx'
import Button from '../common/Button/Button.tsx'
import Card from '../common/Card/Card.tsx'
import Loader from '../common/Loader/Loader.tsx'
import { Pagination } from '../common/Pagination'
import { usePaginatedQuery } from '../common/Pagination/usePaginatedQuery.ts'
import Popup from '../common/Popup/Popup.tsx'
import Table from '../common/Table/Table.tsx'
import FilterSection from './FilterSection.tsx'

const fetchPatientProfiles = async ({
  page,
  filters
}: {
  page: number
  filters?: string
}): Promise<PatientProfileResponse | null> => {
  const filterParams = `?page=${page}${filters ? `${filters}` : ''}`
  const res =
    await patientProfilesApi.getList<PatientProfileResponse>(filterParams)

  return res.data
}

const PatientProfiles = ({
  user_assignment_code
}: {
  user_assignment_code?: string
}) => {
  const { state } = useAuth()
  const { deletePatientProfileMutation } = usePatientProfileMutations({
    onSuccess: () => {
      setDeletePatientProfile(null)
      setShowDeleteConfirmation(false)
      setDeleteID(null)
    }
  })

  const [filterParams, setFilterParams] = useState<string | undefined>(
    undefined
  )
  const [selectedRow, setSelectedRow] = useState<PatientProfile | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [deleteID, setDeleteID] = useState<number | null>(null)
  const [deletePatientProfile, setDeletePatientProfile] = useState<PatientProfile | null>(null)

  const navigate = useNavigate()
  const title = user_assignment_code
    ? `Lista profili przypisanych do kodu - ${user_assignment_code}`
    : 'Lista pacjentów'

  useLayoutEffect(() => {
    if (user_assignment_code)
      setFilterParams(`&user_assignment_code=${user_assignment_code}`)
  }, [])

  const { data, isLoading, isError, currentPage, goToPage, totalPages } =
    usePaginatedQuery<PatientProfileResponse>({
      queryKey: [
        'PatientProfiles',
        state.isAuthenticated,
        state.email,
        filterParams
      ],
      fetcher: ({ page, filterParams }) =>
        fetchPatientProfiles({ page, filters: filterParams }),
      pageSize: 10,
      filterParams
    })

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  useEffect(() => {
    goToPage(1)
  }, [filterParams])

  const menuItems: ActionPopupMenuItem<PatientProfile>[] = [
    {
      label: 'Dane pacjenta',
      icon: <CiSearch className="text-lg" />,
      onClick: (row) => {
        navigate(`/patient-profile/${row.id}/`)
      }
    },
    {
      label: 'Usuń',
      icon: <BiSolidTrash className="text-lg" color="red" />,
      className: 'text-red-600',
      onClick: (row) => {
        setDeleteID(row.id)
        setDeletePatientProfile(row)
        setShowDeleteConfirmation(true)
      }
    }
  ]

  const columns = useMemo(() => setPatientProfileTableColumns(), [])
  const actions = setPatientProfileTableActions((row, event) => {
    const rect = event.currentTarget.getBoundingClientRect()

    setSelectedRow(row)
    setMenuPosition({
      top: rect.bottom + 6,
      left: rect.right - 180
    })
    setIsMenuOpen(true)
  })

  const resetOnCancel = () => {
    setShowDeleteConfirmation(false)
    setDeleteID(null)
  }

  return (
    <React.Fragment>
      <FilterSection
        setFilterParams={setFilterParams}
        userAssignmentCode={user_assignment_code}
      />

      <Card className="mt-3">
        <Card.Title>
          <div className={'flex flex-row justify-between items-center'}>
            {title}

            <Button
              variant="filled"
              size="medium"
              color={'primary'}
              type="button"
              icon={<AiOutlinePlus />}
              onClick={() => navigate('/patient-profile/create')}
            >
              {'Dodaj nowego pacjenta'}
            </Button>
          </div>
        </Card.Title>
        <Card.Body>
          {isLoading && <Loader label={'Pobieranie danych...'} />}

          {data && !isLoading && data.results.length > 0 && (
            <>
              <Table data={data.results} columns={columns} actions={actions} />

              <ActionPopupMenu
                row={selectedRow}
                isOpen={isMenuOpen}
                items={menuItems}
                position={menuPosition}
                onClose={() => setIsMenuOpen(false)}
              />

              <div className="mt-4 text-right">{`Liczba profili: ${data.count || 0}`}</div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={goToPage}
              />
            </>
          )}

          {!isLoading && data && data.results.length === 0 && (
            <div>
              <Alert
                type="info"
                message="Brak danych"
                icon={<AiOutlineInfoCircle className="text-lg" />}
              />
            </div>
          )}
        </Card.Body>
      </Card>
      <Popup
        size={'auto'}
        isVisible={showDeleteConfirmation}
        onClose={resetOnCancel}
      >
        <p className="text-xl font-bold p-6 uppercase">Uwaga</p>
        <p className="text-xl px-6 mb-2">
          Wszystkie dane pacjenta
          <span className={'font-bold'}>
            {` (${deletePatientProfile?.first_name} ${deletePatientProfile?.last_name}) `}
          </span>
          zostaną usunięte i nie będzie możliwości ich przywrócenia.
        </p>
        <div className="w-full flex justify-end my-8 gap-x-4 px-8">
          <Button
            variant="filled"
            size="medium"
            color="primary"
            type="button"
            onClick={resetOnCancel}
            className="px-4 bg-yellow-500 text-white rounded-md shadow-md hover:bg-gray-400 transition-colors"
          >
            Anuluj
          </Button>
          <Button
            variant="filled"
            size="medium"
            color="primary"
            type="button"
            onClick={() =>
              deleteID && deletePatientProfileMutation.mutate(deleteID)
            }
            className="px-4 bg-green-700 text-white rounded-md shadow-md hover:bg-green-600 transition-colors"
          >
            Usuń
          </Button>
        </div>
      </Popup>
    </React.Fragment>
  )
}

export default PatientProfiles
